import {
    Box, HStack, Input, Button, Table, Thead, Tbody, Tr, Th, Td, Text, Flex, Image, Select,
    Badge, Progress, Alert, AlertIcon, AlertTitle, Tooltip, useToast,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import { Bot, Cog, ImageOff, ChevronRight, ChevronDown } from 'lucide-react';
import { getLogsImagens } from "../services/api";
import { useState, useEffect, Fragment } from "react";
import Preloader from "../components/Preloader";
import ScrollToTop from "../components/ScrollToTop";
import { useUsuarios, semBloqueados } from "../hooks/useUsuarios";

const ACOES = {
    upload: 'Upload manual',
    importar_url: 'Importou via URL',
    aprovar: 'Aprovou',
    rejeitar: 'Reprovou',
    pendente: 'Marcou pendente',
    principal: 'Definiu principal',
    editar: 'Editou',
    reordenar: 'Reordenou',
    remover: 'Removeu',
    restaurar: 'Restaurou',
    remover_permanente: 'Excluiu permanente',
    bloquear: 'Bloqueou',
    desbloquear: 'Desbloqueou',
    ia_solicitar_analise: 'Solicitou análise IA',
    ia_solicitar_correcao: 'Solicitou correção IA',
    ia_feedback: 'Feedback IA',
    ia_analise: 'IA analisou',
    ia_aprovacao: 'IA aprovou',
    ia_importacao: 'IA importou',
    ia_substituicao: 'IA substituiu',
    ia_sem_correcao: 'IA sem correção',
    sync_ml: 'Sync Mercado Livre',
    sync_ecommerce: 'Sync Site',
    remover_bloqueio: 'Excluída por bloqueio',
};

const labelAcao = (a) => ACOES[a] || a;

const getImgUser = (usuario_id) => {
    const mapa = {
        3: "/gabriel_logo.webp",
        5: "/tilio_logo.webp",
        6: "/riccioppo_logo.webp",
        7: "/user.png",
        8: "/gabriel_valiente_logo.webp",
        9: "/emerson_vendedor_logo.webp",
        10: "/analuiza_logo.webp",
    };
    return mapa[usuario_id] || "taga_logo.jpg";
};

const LogsImagens = () => {
    const [loading, setLoading] = useState(false);
    const [loadingLinear, setLoadingLinear] = useState(false);
    const [logs, setLogs] = useState([]);
    const [semRegistros, setSemRegistros] = useState(false);

    const [pagina, setPagina] = useState(1);
    const [ultimaPagina, setUltimaPagina] = useState(1);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [paginateFrom, setPaginateFrom] = useState(0);
    const [paginateTo, setPaginateTo] = useState(0);
    const [expandidos, setExpandidos] = useState(() => new Set());

    const [ator, setAtor] = useState("");
    const [produtoId, setProdutoId] = useState("");
    const [imagemId, setImagemId] = useState("");
    const [acao, setAcao] = useState("");
    const [resultado, setResultado] = useState("");
    const [usuario, setUsuario] = useState("");
    const usuarios = useUsuarios();
    const [dataInicial, setDataInicial] = useState("");
    const [dataFinal, setDataFinal] = useState("");

    const toast = useToast();
    const hoje = new Date().toISOString().split('T')[0];

    useEffect(() => {
        consultar(1, ator);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const buildParams = (paginaAux, atorAux) => {
        const params = { page: paginaAux };
        if (atorAux) params.ator = atorAux;
        if (produtoId) params.produto_id = produtoId;
        if (imagemId) params.imagem_id = imagemId;
        if (acao) params.acao = acao;
        if (resultado) params.resultado = resultado;
        if (usuario) params.user_id = usuario;
        if (dataInicial) params.data_inicial = dataInicial;
        if (dataFinal) params.data_final = dataFinal;
        return params;
    };

    const consultar = async (paginaAux, atorAux = ator) => {
        setLoadingLinear(true);
        setLoading(true);
        try {
            const res = await getLogsImagens(buildParams(paginaAux, atorAux));
            const d = res?.data;
            if (d && Array.isArray(d.data)) {
                setLogs(d.data);
                setExpandidos(new Set());
                setUltimaPagina(d.last_page || 1);
                setTotalRegistros(d.total || 0);
                setPaginateFrom(d.from || 0);
                setPaginateTo(d.to || 0);
                setSemRegistros(d.data.length === 0);
            } else {
                setLogs([]);
                setSemRegistros(true);
            }
        } catch {
            toast({ title: 'Erro ao carregar logs de imagens.', status: 'error', isClosable: true });
            setLogs([]);
            setSemRegistros(true);
        } finally {
            setLoadingLinear(false);
            setLoading(false);
        }
    };

    const trocarAba = (novoAtor) => {
        setAtor(novoAtor);
        setPagina(1);
        consultar(1, novoAtor);
    };

    const pesquisar = () => {
        setPagina(1);
        consultar(1, ator);
    };

    const limpar = () => {
        setProdutoId("");
        setImagemId("");
        setAcao("");
        setResultado("");
        setUsuario("");
        setDataInicial("");
        setDataFinal("");
        setPagina(1);
        setTimeout(() => consultar(1, ator), 0);
    };

    const irPara = (p) => {
        const alvo = Math.min(Math.max(p, 1), ultimaPagina);
        setPagina(alvo);
        consultar(alvo, ator);
    };

    const formataData = (data) => {
        if (!data) return '';
        const options = {
            timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        };
        return new Date(data).toLocaleString('pt-BR', options);
    };

    const badgeResultado = (r) => {
        if (!r) return null;
        const cor = ['sucesso', 'ok'].includes(r) ? 'green'
            : ['erro', 'falha'].includes(r) ? 'red'
                : r === 'divergente' ? 'yellow' : 'gray';
        return <Badge colorScheme={cor} fontSize="10px">{r}</Badge>;
    };

    const iconeAtor = (log) => {
        if (log.ator === 'ia') {
            return (
                <Tooltip label="Inteligência Artificial">
                    <Flex align="center" justify="center" boxSize="30px" borderRadius="50%" bg="purple.100" mx="auto">
                        <Bot size={16} color="#6B46C1" />
                    </Flex>
                </Tooltip>
            );
        }
        if (log.ator === 'sistema') {
            return (
                <Tooltip label="Sistema (sincronização)">
                    <Flex align="center" justify="center" boxSize="30px" borderRadius="50%" bg="gray.200" mx="auto">
                        <Cog size={16} color="#4A5568" />
                    </Flex>
                </Tooltip>
            );
        }
        return (
            <Tooltip label={log.user_name || 'Usuário'}>
                <Image
                    boxShadow="lg" borderRadius="50%" mx="auto"
                    src={getImgUser(log.user_id)}
                    alt="usuario" boxSize="30px" objectFit="cover"
                    fallbackSrc="taga_logo.jpg"
                />
            </Tooltip>
        );
    };

    const miniatura = (log) => {
        const url = log.imagem_url || log.produto_imagem_url;
        if (!url) {
            return (
                <Flex align="center" justify="center" boxSize="40px" borderRadius="md" bg="gray.100" mx="auto">
                    <ImageOff size={16} color="#A0AEC0" />
                </Flex>
            );
        }
        return (
            <a href={url} target="_blank" rel="noreferrer">
                <Image
                    src={url} alt="imagem" boxSize="40px" objectFit="cover"
                    borderRadius="md" mx="auto" border="1px solid #e2e8f0"
                    fallbackSrc="taga_logo.jpg"
                />
            </a>
        );
    };

    const toggleExpandir = (id) => {
        setExpandidos((prev) => {
            const novo = new Set(prev);
            if (novo.has(id)) novo.delete(id); else novo.add(id);
            return novo;
        });
    };

    const detalheItem = (rotulo, valor) => (
        <Box minW="120px">
            <Text fontSize="9px" color="gray.500" fontWeight="bold">{rotulo}</Text>
            <Text fontSize="13px" color="gray.800">{valor}</Text>
        </Box>
    );

    const renderDetalhes = (log) => {
        const d = log.detalhes && typeof log.detalhes === 'object' ? log.detalhes : null;
        return (
            <Box textAlign="start" bg="gray.50" borderRadius="md" p={4} m={2}>
                <Flex flexWrap="wrap" gap={5}>
                    {detalheItem('AÇÃO', labelAcao(log.acao))}
                    {detalheItem('ATOR', log.ator)}
                    {detalheItem('CANAL', log.canal || '-')}
                    <Box minW="120px">
                        <Text fontSize="9px" color="gray.500" fontWeight="bold">RESULTADO</Text>
                        {badgeResultado(log.resultado) || <Text fontSize="13px">-</Text>}
                    </Box>
                    {detalheItem('IMAGEM ID', log.imagem_id || '-')}
                    {detalheItem('PRODUTO', log.produto_id ? `${log.produto_id}${log.produto_nome ? ' — ' + log.produto_nome : ''}` : '-')}
                    {detalheItem('USUÁRIO', log.user_name || '-')}
                    {detalheItem('DATA', formataData(log.created_at))}
                </Flex>
                <Box mt={4}>
                    <Text fontSize="9px" color="gray.500" fontWeight="bold">DESCRIÇÃO</Text>
                    <Text fontSize="13px" color="gray.800">{log.descricao || '-'}</Text>
                </Box>
                {d && Array.isArray(d.alteracoes) && d.alteracoes.length > 0 && (
                    <Box mt={4}>
                        <Text fontSize="10px" color="#F68633" fontWeight="bold" mb={2}>ALTERAÇÕES</Text>
                        <Box overflowX="auto">
                            <Table size="sm" variant="simple" w="auto">
                                <Thead>
                                    <Tr>
                                        <Th fontSize="10px">Campo</Th>
                                        <Th fontSize="10px">Anterior</Th>
                                        <Th fontSize="10px">Atual</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {d.alteracoes.map((alt, i) => (
                                        <Tr key={i}>
                                            <Td fontSize="12px" fontWeight="bold" color="gray.600" py={1}>{alt.campo}</Td>
                                            <Td fontSize="12px" color="red.600" py={1}>{alt.de === null || alt.de === undefined || alt.de === '' ? '-' : String(alt.de)}</Td>
                                            <Td fontSize="12px" color="green.700" py={1}>{alt.para === null || alt.para === undefined || alt.para === '' ? '-' : String(alt.para)}</Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>
                )}
                {d && Object.keys(d).filter((k) => k !== 'alteracoes').length > 0 && (
                    <Box mt={4}>
                        <Text fontSize="10px" color="#F68633" fontWeight="bold" mb={2}>DETALHES</Text>
                        <Box overflowX="auto">
                            <Table size="sm" variant="simple" w="auto">
                                <Tbody>
                                    {Object.entries(d).filter(([k]) => k !== 'alteracoes').map(([k, v]) => (
                                        <Tr key={k}>
                                            <Td fontSize="11px" fontWeight="bold" color="gray.600" verticalAlign="top" whiteSpace="nowrap" py={1} border="none">{k}</Td>
                                            <Td fontSize="12px" color="gray.800" py={1} border="none">
                                                {v === null || v === undefined ? '-' : (typeof v === 'object' ? JSON.stringify(v) : String(v))}
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    const abas = [
        { key: "", label: "Todos" },
        { key: "humano", label: "Humano" },
        { key: "ia", label: "IA" },
        { key: "sistema", label: "Sync (canais)" },
    ];

    return (
        <>
            <ScrollToTop />
            {loading && <Preloader />}
            <Box textAlign="center" fontSize="xl" py={10} bg="white" minH="100vh">
                <Box textAlign="center" ml={4} mb={0} fontSize="xl">
                    <Text color={"#F68633"} fontWeight={"bold"}>
                        LOGS DE IMAGENS
                    </Text>
                </Box>

                <Box mx={{ base: 1, md: 12, lg: 20 }} mt={0} boxShadow='md' borderRadius="lg" px={0} mb={"10"} pb={2}>
                    <HStack spacing={3} pt={4} px={4} flexWrap="wrap">
                        {abas.map((a) => (
                            <Button
                                key={a.key || 'todos'}
                                onClick={() => trocarAba(a.key)}
                                variant={ator === a.key ? "solid" : "ghost"}
                                bg={ator === a.key ? "orange.100" : "transparent"}
                                color={ator === a.key ? "orange.800" : "gray.600"}
                                colorScheme="orange"
                                boxShadow={ator === a.key ? "md" : "none"}
                                borderRadius="lg"
                                size="md"
                            >
                                {a.label}
                            </Button>
                        ))}
                    </HStack>

                    <Box mb={8}>
                        <Text fontSize={"16px"} textAlign="start" ml={4} mt={8} color={"#F68633"} fontWeight={"bold"}>
                            FILTROS
                        </Text>
                        <Flex align={"flex-end"} justifyContent={"center"} flexWrap="wrap" gap={3} px={4}>
                            <Box>
                                <Text textAlign="start" mt={2} color={"#F68633"} fontSize={"10px"} fontWeight={"bold"}>PRODUTO (SKU)</Text>
                                <Input boxShadow='lg' bg='white' color="black" w={{ base: "140px", md: "120px" }}
                                    value={produtoId} onChange={(e) => setProdutoId(e.target.value)} />
                            </Box>
                            <Box>
                                <Text textAlign="start" mt={2} color={"#F68633"} fontSize={"10px"} fontWeight={"bold"}>IMAGEM (ID)</Text>
                                <Input boxShadow='lg' bg='white' color="black" w={{ base: "140px", md: "120px" }}
                                    value={imagemId} onChange={(e) => setImagemId(e.target.value)} />
                            </Box>
                            <Box>
                                <Text textAlign="start" mt={2} color={"#F68633"} fontSize={"10px"} fontWeight={"bold"}>AÇÃO</Text>
                                <Select boxShadow='lg' w={{ base: "200px", md: "180px" }} value={acao} onChange={(e) => setAcao(e.target.value)}>
                                    <option value="">Todas</option>
                                    <optgroup label="Humano">
                                        {['upload', 'importar_url', 'aprovar', 'rejeitar', 'pendente', 'principal', 'editar', 'reordenar', 'remover', 'restaurar', 'remover_permanente', 'bloquear', 'desbloquear'].map(k => (
                                            <option key={k} value={k}>{ACOES[k]}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="IA">
                                        {['ia_analise', 'ia_aprovacao', 'ia_importacao', 'ia_substituicao', 'ia_sem_correcao', 'ia_solicitar_analise', 'ia_solicitar_correcao', 'ia_feedback'].map(k => (
                                            <option key={k} value={k}>{ACOES[k]}</option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="Sync">
                                        {['sync_ml', 'sync_ecommerce', 'remover_bloqueio'].map(k => (
                                            <option key={k} value={k}>{ACOES[k]}</option>
                                        ))}
                                    </optgroup>
                                </Select>
                            </Box>
                            <Box>
                                <Text textAlign="start" mt={2} color={"#F68633"} fontSize={"10px"} fontWeight={"bold"}>RESULTADO</Text>
                                <Select boxShadow='lg' w={{ base: "140px", md: "130px" }} value={resultado} onChange={(e) => setResultado(e.target.value)}>
                                    <option value="">Todos</option>
                                    <option value="sucesso">Sucesso</option>
                                    <option value="erro">Erro</option>
                                    <option value="ok">OK (IA)</option>
                                    <option value="divergente">Divergente (IA)</option>
                                </Select>
                            </Box>
                            <Box>
                                <Text textAlign="start" mt={2} color={"#F68633"} fontSize={"10px"} fontWeight={"bold"}>USUÁRIO</Text>
                                <Select boxShadow='lg' w={{ base: "160px", md: "150px" }} value={usuario} onChange={(e) => setUsuario(e.target.value)}>
                                    <option value="">Todos</option>
                                    {semBloqueados(usuarios).map((u) => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                </Select>
                            </Box>
                            <Box>
                                <Text textAlign="start" mt={2} color={"#F68633"} fontSize={"10px"} fontWeight={"bold"}>DATA INICIAL</Text>
                                <Input boxShadow='lg' type="date" max={hoje} w={{ base: "150px", md: "150px" }}
                                    value={dataInicial} onChange={(e) => setDataInicial(e.target.value)} />
                            </Box>
                            <Box>
                                <Text textAlign="start" mt={2} color={"#F68633"} fontSize={"10px"} fontWeight={"bold"}>DATA FINAL</Text>
                                <Input boxShadow='lg' type="date" max={hoje} w={{ base: "150px", md: "150px" }}
                                    value={dataFinal} onChange={(e) => setDataFinal(e.target.value)} />
                            </Box>
                            <Box>
                                <Button bg={"#01695E"} color={"white"} boxShadow='lg' onClick={pesquisar}
                                    _hover={{ bg: "white", color: "black" }}>
                                    <FaSearch fontSize='12px' />
                                </Button>
                                <Button ml={2} bg={"#FF0000"} color={"white"} boxShadow='lg' onClick={limpar}
                                    _hover={{ bg: "white", color: "black" }}>
                                    X
                                </Button>
                            </Box>
                        </Flex>
                    </Box>

                    {semRegistros && !loading && (
                        <Alert status='warning' mx={4} w="auto" borderRadius="md">
                            <AlertIcon />
                            <AlertTitle>Nenhum registro encontrado!</AlertTitle>
                        </Alert>
                    )}

                    <Box overflowX="auto" mt={6} mx={{ base: "0", sm: "2", md: "6" }}>
                        <Table size="sm">
                            <Thead bg={"#bfbdbd"}>
                                <Tr>
                                    <Th w="24px" px={1}></Th>
                                    <Th textAlign="center" color="#01695E">Imagem</Th>
                                    <Th textAlign="center" color="#01695E">Quem</Th>
                                    <Th textAlign="center" color="#01695E">Ação</Th>
                                    <Th textAlign="center" color="#01695E">Produto</Th>
                                    <Th textAlign="center" color="#01695E">Descrição</Th>
                                    <Th textAlign="center" color="#01695E">Resultado</Th>
                                    <Th textAlign="center" color="#01695E">Data</Th>
                                </Tr>
                            </Thead>
                            <Tbody color={"black"}>
                                {!loading && logs.map((log) => (
                                    <Fragment key={log.id}>
                                    <Tr _hover={{ background: "#f0f0f0" }} cursor="pointer" onClick={() => toggleExpandir(log.id)}>
                                        <Td px={1} textAlign="center">
                                            {expandidos.has(log.id)
                                                ? <ChevronDown size={16} color="#01695E" />
                                                : <ChevronRight size={16} color="#A0AEC0" />}
                                        </Td>
                                        <Td textAlign="center">{miniatura(log)}</Td>
                                        <Td textAlign="center">{iconeAtor(log)}</Td>
                                        <Td textAlign="center" fontSize="12px" fontWeight="600">{labelAcao(log.acao)}</Td>
                                        <Td textAlign="center" fontSize="12px">
                                            {log.produto_id ? (
                                                <Box>
                                                    <Text fontWeight="bold">{log.produto_id}</Text>
                                                    {log.produto_nome && (
                                                        <Text fontSize="10px" color="gray.500" noOfLines={1} maxW="160px">{log.produto_nome}</Text>
                                                    )}
                                                </Box>
                                            ) : '-'}
                                        </Td>
                                        <Td textAlign="start" fontSize="12px" maxW="360px">
                                            <Text noOfLines={2}>{log.descricao}</Text>
                                            {log.imagem_id && (
                                                <Text fontSize="10px" color="gray.400">img #{log.imagem_id}</Text>
                                            )}
                                        </Td>
                                        <Td textAlign="center">{badgeResultado(log.resultado)}</Td>
                                        <Td textAlign="center" fontSize="11px">{formataData(log.created_at)}</Td>
                                    </Tr>
                                    {expandidos.has(log.id) && (
                                        <Tr>
                                            <Td colSpan={8} p={0} bg="white">{renderDetalhes(log)}</Td>
                                        </Tr>
                                    )}
                                    </Fragment>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>

                    <Flex justifyContent={"center"} mt={6}>
                        <Flex flexDirection={"column"} align="center">
                            <Text fontSize={'10px'} fontWeight={"bold"}>
                                Mostrando {logs.length} de {totalRegistros} registros ({paginateFrom} ao {paginateTo})
                            </Text>
                            <Flex flexDirection={"row"} mt={1}>
                                <Button size="sm" onClick={() => irPara(1)} isDisabled={pagina === 1}>{"<<"}</Button>
                                <Button size="sm" onClick={() => irPara(pagina - 1)} isDisabled={pagina <= 1}>{"<"}</Button>
                                <Button size="sm" isDisabled>{pagina}</Button>
                                <Button size="sm" onClick={() => irPara(pagina + 1)} isDisabled={pagina >= ultimaPagina}>{">"}</Button>
                                <Button size="sm" onClick={() => irPara(ultimaPagina)} isDisabled={pagina === ultimaPagina}>{">>"}</Button>
                            </Flex>
                        </Flex>
                    </Flex>
                    {loadingLinear && <Progress mt={2} size='xs' isIndeterminate colorScheme="orange" />}
                </Box>
            </Box>
        </>
    );
};

export default LogsImagens;
